//@ts-check
import { validEmail } from './utilFunctions';
import bind from './cart/bind.js';
/**
 * @typedef{{
 *	id: number,
 *	offer_name: string,
 *	customer_name: string | null,
 *	customer_email: string | null,
 *	status: Status,
 *	cart: CartItem[]
 *	comment: string | null,
 *	added_attachments: number[],
 *	available_attachments: Attachment[],
 * }} Configuration
 *
 * @typedef{{
 * 	 product: {id: number},
 * 	 subproduct: {id: number}
 * }} CartItem
 *
 * @typedef{{
 *	selectedConfigurationId: null | number,
 *	configurationStates: null | ConfigurationStates
 * }} CartState
 *
 * @typedef{{
 *  localCartState: Item[],
 *  serverCartState: Item[],
 *  localAttachments: Attachment[],
 *	serverAttachments: Attachment[],
 *	availableAttachments: Attachment[],
 *	serverCustomerName: null | string,
 *	serverCustomerEmail: null | string,
 *	serverComment: null | string,
 * }} ConfigurationStates
 *
 * @typedef{{
 *  id: number,
 *  name: string
 * }} Attachment
 *
 * @typedef{{
 *	id: number
 *	subproductId: null | number
 * }} Item
 *
 * @typedef{{
 *  width: string,
 *  height: string,
 *  depth: string,
 *  glassColor:	string | null,
 *  profileColor: string | null,
 * }} ConfigurationState
 *
 * @typedef{{
 *	name: string,
 *	options: string[],
 * }} ConfigurableDataItem
 *
 * @typedef{{
 *	width: ConfigurableDataItem,
 *	height: ConfigurableDataItem,
 *	depth: ConfigurableDataItem,
 *	glassColor: ConfigurableDataItem,
 *	profileColor: ConfigurableDataItem,
 * }} ConfigurableData
 *
 * @typedef{{
 *  id: number,
 *  width: string | number,
 *  height: string | number,
 *  depth: string | number,
 *  elements: ConfigurationStateElement[]
 *  price: number,
 *  onSalePrice: number,
 * }} Sibling
 *
 * @typedef{{
 *	uvegszin: string | null,
 *	Profilszin: string | null,
 * }} ConfigurationStateElement
 *
 * @typedef{{
 *  status: "success",
 *	configurationOptionState: ConfigurationState | null,
 *	configurationInfo: ConfigurableData | null,
 *	data: {
 *		parent: Parent
 *		siblings: Sibling[],
 *	}
 * }} ItemInfoSuccess
 *
 * @typedef{{
 *  status: "failure",
 *  error: string
 * }} ItemInfoFailure
 *
 * @typedef{{
 * status: "loading"
 * }} ItemInfoLoading
 *
 * @typedef{ItemInfoSuccess | ItemInfoFailure | ItemInfoLoading} ItemInfo
 *
 * @typedef{{
 *	onsale: boolean,
 *	onsalePrice: number,
 *	price: number
 * }} Parent
 *
 * @typedef{'saved' | 'sent' | 'failed' | 'ordered'} Status
 *
 * @typedef{'cart-modal' |
 *	'new-empty-cart-modal' |
 *	'new-cart-modal' |
 *	'send-via-email-modal' |
 *	'send-via-email-first-insert-modal'} Modal
 *
 * @typedef{{
 *	'@cartaction.window': () => void
 * }} CartBind
 */

/**
 * @returns {{
 * 	itemInsertionId: null | number,
 * 	itemInsertionSubproductId: null | number,
 * 	modalToBeClosedId: null | string,
 * 	configurationToBeSelected: null | number,
 * 	itemToBeRemoved: null | number,
 * 	allConfigurations: null | Configuration[],
 * 	cartState: null | CartState
 * 	firstInsertCartState: null | Item,
 * 	firstInsertCartItemInfo: null | ItemInfo,
 * 	itemConfigurationOpen: number,
 * 	firstInsertionItemConfigurationOpen: boolean,
 * 	isConfigurationSelectorOpen: boolean,
 * 	requestCount: number,
 * 	isFetchingConfigurations: boolean,
 * 	configurationNameInput: string,
 * 	configurationErrorMessages: string[],
 * 	emailSendErrorMessages: string[],
 * 	preSelectedOfferId: null | number,
 * 	configurationCustomerName: string,
 * 	configurationCustomerEmail: string,
 * 	configurationComment: string,
 * 	cartItemsInfo: ItemInfo[],
 * 	configurationSearchString: string,
 * 	attachmentSelectorOpen: boolean,
 * 	latestPdfLink: string | null,
 * 	capitalizeFirstLetter: (string: string) => string,
 * 	lookForMatchingStateInSiblings: (c: ConfigurationState, s: Sibling[]) => number | null,
 * 	canSaveProductConfigurationChangesFirstInsert: () => boolean,
 * 	canSaveProductConfigurationChangesFromItemInfo: (i: ItemInfo) => boolean,
 * 	canSaveProductConfigurationChanges: (i: number) => boolean,
 * 	removeAttachment: (a: Attachment) => void,
 * 	isAttachmentAdded: (a: Attachment) => boolean,
 * 	saveProductConfigurationChangesFirstInsert: () => void,
 * 	saveProductConfigurationChanges: (i: number) => void,
 * 	addAttachment: (a: Attachment) => void,
 * 	searchedAllConfigurations: () => Configuration[],
 * 	generateCurrentConfigurationNameString: () => string,
 * 	generatePreSelectedConfigurationNameString: () => string,
 * 	generateItemPriceString: (i: number) => string,
 * 	generateFirstInsertionItemPrice: () => string,
 * 	getPriceFromIndex: (i: number) => number | null,
 * 	getPriceFromItemData: (i: ItemInfo, c: boolean, s: Item) => number | null
 * 	generateSumPriceString: () => string,
 * 	generateConfigurationLabelString: (c: ConfigurationState) => string,
 * 	getConfigurableData: (s: Sibling[]) => ConfigurableData
 * 	prettierPriceTag: (p: number) => string,
 * 	getConfigurationStateFromSibling: (i: number, s: Sibling[]) => ConfigurationState,
 * 	async fetchAllProductData: () => Promise,
 * 	async fetchAllConfigurations: () => Promise,
 * 	isConfigurationStateSameOnServer: () => boolean,
 * 	removeAndGetOfferIdFromQuery: () => number | null,
 * 	initCartState: () => void,
 * 	init: () => void,
 * 	setSelectedConfigurationId: (i: number | null) => void,
 * 	getStatusText: (s: Status) => string,
 *  async createNewConfiguration: () => Promise,
 *  openCreateNewConfiguration: () => void,
 *  async saveCurrentConfiguration: () => Promise,
 *  downloadLatestPdf: () => void,
 *  getOfferName: () => string,
 *  checkEmailFormThen: (f: (n: string, e: string, c: string) => void) => void,
 *  sendViaEmailFirstInsert: () => void,
 *  sendViaEmail: () => void,
 *  modalclose: (i: Modal) => void,
 *  modalCloseConfirmed: (i: Modal) => void,
 *  deleteItem: (i: number) => void,
 *  insertItemIntoNew: () => void,
 *  loadFirstInsertCartItemsInfo: () => void,
 *  bind: CartBind,
 * }}
 */
export default () => ({
	// To bes
	itemInsertionId: null,
	itemInsertionSubproductId: null,
	modalToBeClosedId: null,
	configurationToBeSelected: null,
	itemToBeRemoved: null,

	// Caches
	allConfigurations: null,
	latestPdfLink: null,

	// Cart states
	cartState: null,
	firstInsertCartState: null,
	firstInsertCartItemInfo: null,

	// Ui states
	itemConfigurationOpen: -1,
	firstInsertionItemConfigurationOpen: false,
	isConfigurationSelectorOpen: false,
	requestCount: 0,
	isFetchingConfigurations: false,
	configurationNameInput: '',
	configurationErrorMessages: [],
	emailSendErrorMessages: [],
	preSelectedOfferId: null,
	configurationCustomerName: '',
	configurationCustomerEmail: '',
	configurationComment: '',
	cartItemsInfo: [],
	configurationSearchString: '',
	attachmentSelectorOpen: false,

	// Methods

	capitalizeFirstLetter(string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	},

	lookForMatchingStateInSiblings(configurationState, siblings) {
		const matchingSibling = siblings.find((sibling) => {
			return (
				`${sibling.width}` === configurationState.width &&
				`${sibling.height}` === configurationState.height &&
				`${sibling.depth}` === configurationState.depth &&
				(sibling.elements[0]?.uvegszin || null) ===
				(configurationState.glassColor || null) &&
				(sibling.elements[0]?.Profilszin || null) ===
				(configurationState.profileColor || null)
			);
		});
		return matchingSibling?.id || null;
	},

	canSaveProductConfigurationChangesFirstInsert() {
		if (
			(this.firstInsertCartItemInfo || null) === null ||
			this.firstInsertCartItemInfo === null
		) {
			throw new Error(
				'canSaveProductConfigurationChangesFirstInsert can only be called on valid cart items.'
			);
		}
		return this.canSaveProductConfigurationChangesFromItemInfo(
			this.firstInsertCartItemInfo
		);
	},

	canSaveProductConfigurationChangesFromItemInfo(itemInfo) {
		if (
			itemInfo.status === 'failure' ||
			itemInfo.status === 'loading' ||
			itemInfo.configurationOptionState === null
		) {
			console.warn(
				'canSaveProductConfigurationChanges should only be called on configurable items.'
			);
			return true;
		}
		const sibling = this.lookForMatchingStateInSiblings(
			itemInfo.configurationOptionState,
			itemInfo.data.siblings
		);
		return sibling !== null;
	},

	canSaveProductConfigurationChanges(localStateIndex) {
		if (
			(this.cartItemsInfo[localStateIndex] || null) === null ||
			this.cartItemsInfo[localStateIndex].status !== 'success'
		) {
			throw new Error(
				'canSaveProductConfigurationChanges can only be called on valid cart items.'
			);
		}
		return this.canSaveProductConfigurationChangesFromItemInfo(
			this.cartItemsInfo[localStateIndex]
		);
	},

	removeAttachment(attachment) {
		if (
			this.cartState === null ||
			this.cartState.configurationStates === null
		) {
			throw new Error(
				'removeAttachment can only be called with a valid cart'
			);
		}
		this.cartState.configurationStates.localAttachments =
			this.cartState.configurationStates.localAttachments.filter(
				(att) => att.id !== attachment.id
			);
		if(!this.isAttachmentInAvailable(attachment)){
			this.cartState.configurationStates.availableAttachments.push(attachment);
		}
	},

	isAttachmentAdded(attachment) {
		return (
			this.cartState?.configurationStates?.localAttachments?.find(
				(att) => att.id === attachment.id
			) !== undefined
		);
	},

	isAttachmentInAvailable(attachment) {
		return (
			this.cartState?.configurationStates?.availableAttachments?.find(
				(att) => att.id === attachment.id
			) !== undefined
		);
	},

	saveProductConfigurationChangesFirstInsert() {
		if ((this.firstInsertCartItemInfo || null) === null) {
			throw new Error(
				'saveProductConfigurationChangesFirstInsert can only be called on valid cart items.'
			);
		}
		if (
			this.firstInsertCartItemInfo === null ||
			this.firstInsertCartItemInfo.status === 'failure' ||
			this.firstInsertCartItemInfo.status === 'loading' ||
			this.firstInsertCartItemInfo.configurationOptionState === null
		) {
			throw new Error('Saving is only possible with a valid cart');
		}
		const sibling = this.lookForMatchingStateInSiblings(
			this.firstInsertCartItemInfo.configurationOptionState,
			this.firstInsertCartItemInfo.data.siblings
		);
		if (sibling === null) {
			throw new Error(
				"Can't find matching sibling, please check with canSaveProductConfigurationChanges first."
			);
		}
		if (
			this.firstInsertCartState === null ||
			this.firstInsertCartState.id === null
		) {
			throw new Error(
				'localCartState and cartItemsInfo are out of sync for first insertion, this is a bug, please refresh the page.'
			);
		}
		this.firstInsertCartState.subproductId = sibling;
	},

	saveProductConfigurationChanges(localStateIndex) {
		if (this.cartItemsInfo[localStateIndex] === null) {
			throw new Error(
				'saveProductConfigurationChanges can only be called on valid cart items.'
			);
		}
		const itemInfo = this.cartItemsInfo[localStateIndex];
		if (itemInfo.status !== 'success') {
			throw new Error(
				'saveProductConfigurationChanges can only be called on valid cart items.'
			);
		}
		if (itemInfo.configurationOptionState === null) {
			throw new Error('Saving is only possible with a valid cart');
		}
		const sibling = this.lookForMatchingStateInSiblings(
			itemInfo.configurationOptionState,
			itemInfo.data.siblings
		);
		if (sibling === null) {
			throw new Error(
				"Can't find matching sibling, please check with canSaveProductConfigurationChanges first."
			);
		}
		if (
			this.cartState === null ||
			this.cartState.configurationStates === null ||
			this.cartState.configurationStates.localCartState[
			localStateIndex
			] === null
		) {
			throw new Error(
				'localCartState and cartItemsInfo are out of sync, this is a bug, please refresh the page.'
			);
		}
		this.cartState.configurationStates.localCartState[
			localStateIndex
		].subproductId = sibling;
	},

	addAttachment(attachment) {
		if (
			this.cartState === null ||
			this.cartState.configurationStates === null ||
			this.cartState.configurationStates.availableAttachments == null
		) {
			throw new Error(
				'addAttachment is called without availableAttachments loaded'
			);
		}
		if (
			this.cartState.configurationStates.localAttachments.find(
				(att) => att.id === attachment.id
			) !== undefined
		) {
			return;
		}
		if (
			this.cartState.configurationStates.availableAttachments.find(
				(att) => att.id === attachment.id
			) === undefined
		) {
			throw new Error(
				'addAttachment is called with invalid attachment id'
			);
		}
		this.cartState.configurationStates.localAttachments.push(attachment);
	},

	searchedAllConfigurations() {
		if (this.allConfigurations === null) {
			return [];
		}
		return this.configurationSearchString !== ''
			? this.allConfigurations.filter((configuration) => {
				return configuration.offer_name
					.toLowerCase()
					.includes(this.configurationSearchString.toLowerCase());
			})
			: this.allConfigurations;
	},

	generateCurrentConfigurationNameString() {
		if (this.allConfigurations === null) {
			return '';
		}
		if (this.cartState === null) {
			return '';
		}
		if (this.cartState.selectedConfigurationId === null) {
			return 'Kérjük, válasszon ajánlatot';
		} else {
			const cartState = this.cartState;
			const configuration =
				this.allConfigurations.find(
					(configuration) =>
						configuration.id === cartState.selectedConfigurationId
				) || null;
			if (configuration === null) {
				return '';
			} else {
				return `${configuration.offer_name} (${this.getStatusText(
					configuration.status
				)})`;
			}
		}
	},

	generatePreSelectedConfigurationNameString() {
		if (this.allConfigurations === null) {
			return '';
		}
		if (this.preSelectedOfferId === null) {
			return 'Kérjük, válasszon ajánlatot';
		} else {
			const configuration =
				this.allConfigurations.find(
					(configuration) =>
						configuration.id === this.preSelectedOfferId
				) || null;
			if (configuration === null) {
				return '';
			} else {
				return `${configuration.offer_name} (${this.getStatusText(
					configuration.status
				)})`;
			}
		}
	},

	generateItemPriceString(index, getOriginalPrice = false) {
		if ((this.cartItemsInfo[index] || null) === null) {
			//`getPriceFromIndex was called on an invalid state: index: ${index} is not found.`
			return 'N/A';
		}
		if (this.cartItemsInfo[index].status !== 'success') {
			//`getPriceFromIndex was called on an invalid state: index: ${index} is not in success state.`
			return 'N/A';
		}
		if (
			(this.cartState?.configurationStates?.localCartState[index] ||
				null) === null
		) {
			//`getPriceFromIndex was called on an invalid state: localCartState index: ${index} is not found.`
			return 'N/A';
		}
		const price = this.getPriceFromIndex(index, getOriginalPrice);
		if (price === null) {
			return 'N/A';
		} else {
			return this.prettierPriceTag(price);
		}
	},

	generateFirstInsertionItemPrice() {
		if (this.firstInsertCartState === null) {
			return 'N/A';
		}

		if (this.firstInsertCartItemInfo === null) {
			return 'N/A';
		}

		if (this.firstInsertCartItemInfo.status !== 'success') {
			return 'N/A';
		}

		if (!this.canSaveProductConfigurationChangesFirstInsert()) {
			return 'N/A';
		}

		const price = this.getPriceFromItemData(
			this.firstInsertCartItemInfo,
			this.firstInsertionItemConfigurationOpen,
			{
				id: this.firstInsertCartState.id,
				subproductId: this.firstInsertCartState.subproductId,
			}
		);
		if (price === null) {
			return 'N/A';
		}

		return this.prettierPriceTag(price);
	},

	getPriceFromIndex(index, getOriginalPrice = false) {
		if ((this.cartItemsInfo[index] || null) === null) {
			throw new Error(
				`getPriceFromIndex was called on an invalid state: index: ${index} is not found.`
			);
		}
		if (this.cartItemsInfo[index].status !== 'success') {
			throw new Error(
				`getPriceFromIndex was called on an invalid state: index: ${index} is not in success state.`
			);
		}
		if (
			this.cartState === null ||
			this.cartState.configurationStates === null ||
			this.cartState.configurationStates.localCartState[index] === null
		) {
			throw new Error(
				`getPriceFromIndex was called on an invalid state: localCartState index: ${index} is not found.`
			);
		}

		const itemInfo = this.cartItemsInfo[index];
		return this.getPriceFromItemData(
			itemInfo,
			this.itemConfigurationOpen === index,
			this.cartState.configurationStates.localCartState[index],
			getOriginalPrice
		);
	},

	getPriceFromItemData(itemInfo, isBeingConfigured, itemIds, getOriginalPrice = false) {
		if (itemInfo.status === 'failure' || itemInfo.status === 'loading') {
			throw new Error('itemInfo is not in success state');
		}
		const configurationOptionState = itemInfo.configurationOptionState;
		if (configurationOptionState === null) {
			throw new Error('invalid configurationOptionState');
		}
		if (itemIds.subproductId === null) {
			if(getOriginalPrice){
				return itemInfo.data.parent.price;
			}
			return itemInfo.data.parent.onsale
				? itemInfo.data.parent.onsalePrice || itemInfo.data.parent.price
				: itemInfo.data.parent.price;
		} else if (isBeingConfigured) {
			const siblingId = this.lookForMatchingStateInSiblings(
				configurationOptionState,
				itemInfo.data.siblings
			);
			const sibling = itemInfo.data.siblings.find(
				(sibling) => sibling.id === siblingId
			);
			if (!sibling) {
				return null;
			} else {
				if(getOriginalPrice){
					return sibling.price;
				}
				return itemInfo.data.parent.onsale
					? sibling.onSalePrice || sibling.price
					: sibling.price;
			}
		} else {
			const sibling =
				itemInfo.data.siblings.find(
					(sibling) => sibling.id === itemIds.subproductId
				) || null;
			if (sibling === null) {
				throw new Error('sibling is undefined');
			}
			if(getOriginalPrice){
				return sibling.price;
			}
			return itemInfo.data.parent.onsale
				? sibling.onSalePrice || sibling.price
				: sibling.price;
		}
	},

	cartHasOnSaleItem() {
		if (
			this.cartState === null ||
			this.cartState.configurationStates === null ||
			this.cartState.configurationStates.localCartState === null
		) {
			// generateSumPriceString was called on an invalid state
			return false;
		}

		console.info(this.getSumPriceOfCartItems() !== this.getSumPriceOfCartItems(true));
		return this.getSumPriceOfCartItems() !== this.getSumPriceOfCartItems(true);
	},

	getSumPriceOfCartItems(getOriginalPrice = false) {
		const callback = (sum, _, index) => {
			if (sum === null) {
				return null;
			}
			if (this.cartItemsInfo[index].status !== 'success') {
				return null;
			}
			const price = this.getPriceFromIndex(index, getOriginalPrice);
			if (price === null) {
				return null;
			} else {
				return sum + price;
			}
		};
		return this.cartState.configurationStates.localCartState.reduce(
			callback,
			0
		);
	},

	generateSumPriceString(getOriginalPrice = false) {
		if (
			this.cartState === null ||
			this.cartState.configurationStates === null ||
			this.cartState.configurationStates.localCartState === null
		) {
			// generateSumPriceString was called on an invalid state
			return 'N/A';
		}
		/** @type{(sum: number | null, item: Item, index: number) => number | null} */

		const sum = this.getSumPriceOfCartItems(getOriginalPrice);
		if (sum !== null) {
			return this.prettierPriceTag(sum);
		} else {
			return 'N/A';
		}
	},

	generateConfigurationLabelString(configurationState) {
		if ((configurationState || null) === null) {
			return '';
		}
		let notFirst = false;
		let valueLabel = '';
		let nameLabel = '';
		const whd = [
			{
				name: 'Szél.',
				value: configurationState.width,
			},
			{
				name: 'Mag.',
				value: configurationState.height,
			},
			{
				name: 'Mély.',
				value: configurationState.depth,
			},
		];
		for (const whdItem of whd) {
			if (!!whdItem.value && whdItem.value != '0') {
				if (notFirst) {
					valueLabel += ' \u00d7 ';
					nameLabel += ' \u00d7 ';
				}
				valueLabel += `${whdItem.value} cm`;
				nameLabel += `${whdItem.name}`;
				notFirst = true;
			}
		}
		let extraLabel = '';
		if (!!configurationState.glassColor) {
			extraLabel += `Üvegszín: ${this.capitalizeFirstLetter(
				configurationState.glassColor
			)}${!!configurationState.profileColor ? ', ' : ''}`;
		}
		if (!!configurationState.profileColor) {
			extraLabel += `Profilszín: ${this.capitalizeFirstLetter(
				configurationState.profileColor
			)}`;
		}
		return `Méretek: ${valueLabel} (${nameLabel})\n${extraLabel}`;
	},

	getConfigurableData(siblings) {
		const configurableDataDict = {
			width: { name: 'Szélesség', options: [] },
			height: { name: 'Magasság', options: [] },
			depth: { name: 'Mélység', options: [] },
			glassColor: { name: 'Üvegszín', options: [] },
			profileColor: { name: 'Profilszín', options: [] },
		};
		siblings.forEach((sibling) => {
			const width = sibling.width;
			const height = sibling.height;
			const depth = sibling.depth;
			const glassColor = sibling.elements[0]?.uvegszin || null;
			const profileColor = sibling.elements[0]?.Profilszin || null;

			/** @type{(array: {value: string, name: string}[], value: string | number, name: string) => void} */
			const addIfNotThere = (array, value, name) => {
				if (
					!!value &&
					array.findIndex((option) => {
						return option.value === `${value}`;
					}) === -1
				) {
					array.push({ value: `${value}`, name: name });
				}
			};
			addIfNotThere(
				configurableDataDict.width.options,
				width,
				`${width} cm`
			);
			addIfNotThere(
				configurableDataDict.height.options,
				height,
				`${height} cm`
			);
			addIfNotThere(
				configurableDataDict.depth.options,
				depth,
				`${depth} cm`
			);
			if (glassColor !== null) {
				addIfNotThere(
					configurableDataDict.glassColor.options,
					glassColor,
					this.capitalizeFirstLetter(glassColor)
				);
			}
			if (profileColor !== null) {
				addIfNotThere(
					configurableDataDict.profileColor.options,
					profileColor,
					this.capitalizeFirstLetter(profileColor)
				);
			}
		});
		return configurableDataDict;
	},

	prettierPriceTag(price) {
		return price.toLocaleString('hu-HU', {
			style: 'currency',
			currency: 'HUF',
			maximumFractionDigits: 0,
		});
	},

	getConfigurationStateFromSibling(siblingId, siblings) {
		const sibling =
			siblings.find((sibling) => {
				return sibling.id === siblingId;
			}) || null;
		if (sibling === null) {
			throw new Error(
				`Could not find subproduct with id ${siblingId} in the product.`
			);
		}
		return {
			width: `${sibling.width}`,
			height: `${sibling.height}`,
			depth: `${sibling.depth}`,
			glassColor: sibling.elements[0]?.uvegszin
				? `${sibling.elements[0]?.uvegszin}`
				: null,
			profileColor: sibling.elements[0]?.Profilszin
				? `${sibling.elements[0]?.Profilszin}`
				: null,
		};
	},

	async fetchAllProductData() {
		if (
			this.cartState === null ||
			this.cartState.configurationStates === null
		) {
			throw new Error(
				'fetchAllProductData can only be called when there is a configuration loaded'
			);
		}
		this.cartItemsInfo = Array(
			this.cartState.configurationStates.localCartState.length
		).fill({ status: 'loading' });
		const configurationStates = this.cartState.configurationStates;
		this.$nextTick(() => {
			const promises = configurationStates.localCartState.map(
				async (ids, index) => {
					const parentId = ids.id;
					const siblingId = ids.subproductId;
					this.requestCount++;
					return fetch(
						`/api/products/${parentId}/siblings-and-parent`,
						{
							method: 'GET',
							headers: {
								accept: 'application/json',
								'content-type': 'application/json',
							},
						}
					)
						.then((resp) => resp.json())
						.then((json) => {
							this.requestCount--;
							if (siblingId === null) {
								this.cartItemsInfo[index] = {
									status: 'success',
									data: json.data,
									configurationInfo: null,
									configurationOptionState: null,
								};
							} else {
								this.cartItemsInfo[index] = {
									status: 'success',
									data: json.data,
									configurationInfo: this.getConfigurableData(
										json.data.siblings
									),
									configurationOptionState:
										this.getConfigurationStateFromSibling(
											siblingId,
											json.data.siblings
										),
								};
							}
						})
						.catch((error) => {
							this.requestCount--;
							console.warn(
								`There was an error fetching product data for id ${parentId}.`,
								error
							);
							this.cartItemsInfo[index] = {
								status: 'failure',
								error: error,
							};
						});
				}
			);
			return Promise.all(promises);
		});
	},

	async fetchAllConfigurations() {
		this.isFetchingConfigurations = true;
		return fetch(`/api/offers`, {
			method: 'GET',
			headers: {
				accept: 'application/json',
				'content-type': 'application/json',
			},
		})
			.then((resp) => resp.json())
			.then((json) => {
				this.allConfigurations = json?.data?.offers || null;
				this.isFetchingConfigurations = false;
			})
			.catch((error) => {
				console.warn(
					'There was an error fetching all configurations.',
					error
				);
				this.isFetchingConfigurations = false;
			});
	},

	isConfigurationStateSameOnServer() {
		if (
			this.cartState === null ||
			this.cartState.configurationStates === null
		) {
			throw new Error(
				'isConfigurationStateSameOnServer can only be called when there is a configuration on the server'
			);
		}
		const configurationStates = this.cartState.configurationStates;
		return (
			this.cartState.configurationStates.localCartState.every(
				(ids, index) =>
					configurationStates.serverCartState[index] &&
					ids.id === configurationStates.serverCartState[index].id &&
					ids.subproductId ===
					configurationStates.serverCartState[index].subproductId
			) &&
			(this.configurationCustomerName || null) ===
			this.cartState.configurationStates.serverCustomerName &&
			(this.configurationCustomerEmail || null) ===
			this.cartState.configurationStates.serverCustomerEmail &&
			(this.configurationComment || null) ===
			this.cartState.configurationStates.serverComment &&
			this.cartState.configurationStates.localAttachments.every(
				(val, index) =>
					val.id === configurationStates.serverAttachments[index].id
			)
		);
	},

	removeAndGetOfferIdFromQuery() {
		const url = new URL(window.location.toString());
		const offer = url.searchParams.get('offer');
		url.searchParams.delete('offer');
		window.history.pushState({}, '', url);
		if (offer !== null) {
			return parseInt(offer) || null;
		} else {
			return null;
		}
	},

	initCartState() {
		if (this.allConfigurations !== null) {
			const selectedConfigurationIdStr = sessionStorage.getItem(
				'selectedConfigurationId'
			);
			const sessionSelectedConfigurationId =
				selectedConfigurationIdStr === null
					? null
					: parseInt(selectedConfigurationIdStr) || null;
			const offerQueryId = this.removeAndGetOfferIdFromQuery();
			if (
				sessionSelectedConfigurationId === null &&
				offerQueryId === null
			) {
				this.cartState = {
					selectedConfigurationId: null,
					configurationStates: null,
				};
			} else {
				const currentCart =
					this.allConfigurations.find(
						(cart) =>
							cart.id ===
							(offerQueryId === null
								? sessionSelectedConfigurationId
								: offerQueryId)
					) || null;
				if (currentCart === null) {
					this.cartState = {
						selectedConfigurationId: null,
						configurationStates: null,
					};
				} else {
					this.cartState = {
						selectedConfigurationId:
							offerQueryId === null
								? sessionSelectedConfigurationId
								: offerQueryId,
						configurationStates: {
							serverCartState: currentCart.cart.map((item) => ({
								id: item.product.id,
								subproductId: item.subproduct?.id || null,
							})),
							serverCustomerName: currentCart.customer_name,
							serverCustomerEmail: currentCart.customer_email,
							serverComment: currentCart.comment,
							serverAttachments: currentCart.added_attachments,
							availableAttachments:
								currentCart.available_attachments,
							localCartState: currentCart.cart.map((item) => ({
								id: item.product.id,
								subproductId: item.subproduct?.id || null,
							})),
							localAttachments: [
								...currentCart.added_attachments,
							],
						},
					};
					this.configurationCustomerName =
						currentCart.customer_name || '';
					this.configurationCustomerEmail =
						currentCart.customer_email || '';
					this.configurationComment = currentCart.comment || '';
					this.fetchAllProductData();
					if (offerQueryId !== null) {
						sessionStorage.setItem(
							'selectedConfigurationId',
							`${offerQueryId}`
						);
						this.$dispatch('modalopenid', {
							modalid: 'cart-modal',
						});
					}
				}
			}
		} else {
			throw new Error(
				'allConfigurations was null when calling initCartState.'
			);
		}
	},

	init() {
		this.$store.cartinfo.getCartInfoText = () => {
			return this.cartState === null ||
				this.cartState.selectedConfigurationId === null
				? 'Ajánlat létrehozása'
				: this.cartState?.configurationStates?.localCartState?.length ||
					0 > 0
					? `${this.cartState.configurationStates.localCartState.length
					} tétel, ${this.generateSumPriceString()}`
					: 'Üres a kosár.';
		};
		this.fetchAllConfigurations().then(() => this.initCartState());
	},

	setSelectedConfigurationId(id) {
		if (this.cartState === null || this.allConfigurations === null) {
			throw new Error(
				'setSelectedConfigurationId was used before initCartState'
			);
		}
		if (id === null) {
			this.cartState = {
				selectedConfigurationId: null,
				configurationStates: null,
			};
			sessionStorage.removeItem('selectedConfigurationId');
		} else {
			const currentCart =
				this.allConfigurations.find((cart) => cart.id === id) || null;
			if (currentCart === null) {
				this.cartState = {
					selectedConfigurationId: null,
					configurationStates: null,
				};
				sessionStorage.removeItem('selectedConfigurationId');
			} else {
				this.cartState = {
					selectedConfigurationId: id,
					configurationStates: {
						serverCartState: currentCart.cart.map((item) => ({
							id: item.product.id,
							subproductId: item.subproduct?.id || null,
						})),

						serverCustomerName: currentCart.customer_name,
						serverCustomerEmail: currentCart.customer_email,
						serverComment: currentCart.comment,
						serverAttachments: currentCart.added_attachments,
						availableAttachments: currentCart.available_attachments,
						localCartState: currentCart.cart.map((item) => ({
							id: item.product.id,
							subproductId: item.subproduct?.id || null,
						})),

						localAttachments: [...currentCart.added_attachments],
					},
				};
				sessionStorage.setItem('selectedConfigurationId', `${id}`);
				this.configurationCustomerName =
					currentCart.customer_name || '';
				this.configurationCustomerEmail =
					currentCart.customer_email || '';
				this.configurationComment = currentCart.comment || '';
				this.fetchAllProductData();
			}
		}
	},

	getStatusText(status) {
		switch (status) {
			case 'saved':
				return 'mentve';
			case 'sent':
				return 'kiküldve';
			case 'failed':
				return 'meghiúsult';
			case 'ordered':
				return 'megrendelve';
			default:
				return '';
		}
	},

	async createNewConfiguration() {
		this.configurationErrorMessages = [];
		const normalizedName = this.configurationNameInput.trim();
		if (normalizedName.length >= 3) {
			this.requestCount++;
			return fetch(`/api/offers`, {
				method: 'POST',
				headers: {
					accept: 'application/json',
					'content-type': 'application/json',
				},
				body: JSON.stringify({
					offer_name: normalizedName,
					cart:
						this.firstInsertCartState !== null
							? [
								{
									id: this.firstInsertCartState.id,
									subproductId:
										this.firstInsertCartState
											.subproductId,
								},
							]
							: [],
				}),
			})
				.then((resp) => resp.json())
				.then((json) => {
					this.itemInsertionId = null;
					this.itemInsertionSubproductId = null;
					this.requestCount--;
					this.$dispatch('notification', {
						message: 'Ajánlat létrehozva',
						status: 'success',
					});
					this.latestPdfLink = json?.data?.offer?.pdf_link;
					this.fetchAllConfigurations().then(() => {
						this.setSelectedConfigurationId(json?.data?.offer.id);
						this.$dispatch('modalcloseid', {
							modalid: 'new-cart-modal',
						});
						this.$dispatch('modalcloseid', {
							modalid: 'new-empty-cart-modal',
						});
						this.$dispatch('modalopenid', {
							modalid: 'cart-modal',
						});
					});
					this.configurationNameInput = '';
				})
				.catch(() => {
					console.warn(`There was an error saving offer`);
					this.configurationErrorMessages = [
						'Hiba történt a konfiburáció mentésekor. Kérjük, próbálja újra később.',
					];
					this.requestCount--;
					this.$dispatch('notification', {
						message:
							'Ajánlat létrehozása alatt ismeretlen hiba történt',
						status: 'error',
					});
				});
		} else {
			this.configurationErrorMessages = [
				'Kérem, adjon nevet a konfigurációnak. (A névnek három karakternél hosszabbnak kell lennie.)',
			];
			return Promise.reject();
		}
	},

	openCreateNewConfiguration() {
		this.$dispatch('modalcloseid', { modalid: 'cart-modal' });
		this.cartState = {
			selectedConfigurationId: null,
			configurationStates: null,
		};
		this.configurationNameInput = '';
		this.configurationErrorMessages = [];
		this.itemInsertionId = null;
		this.itemInsertionSubproductId = null;
		this.$dispatch('modalopenid', { modalid: 'new-empty-cart-modal' });
	},

	async saveCurrentConfiguration() {
		if (
			this.cartState === null ||
			this.cartState.configurationStates === null ||
			this.cartState.selectedConfigurationId === null
		) {
			throw new Error('Cart state or configState is null');
		}
		const selectedConfigurationId = this.cartState.selectedConfigurationId;
		this.configurationErrorMessages = [];
		if (this.configurationCustomerName.length < 1) {
			this.configurationErrorMessages = [];
		}
		if ((this.cartState?.selectedConfigurationId || null) !== null) {
			this.requestCount++;
			return fetch(
				`/api/offers/${this.cartState.selectedConfigurationId}`,
				{
					method: 'PATCH',
					headers: {
						accept: 'application/json',
						'content-type': 'application/json',
					},
					body: JSON.stringify({
						offer_name: this.getOfferName(),
						cart: this.cartState.configurationStates.localCartState,
						customer_name: this.configurationCustomerName.trim(),
						customer_email: this.configurationCustomerEmail.trim(),
						comment: this.configurationComment.trim(),
						attachments:
							this.cartState.configurationStates.localAttachments.map(att => att.id),
					}),
				}
			)
				.then((resp) => resp.json())
				.then((json) => {
					this.fetchAllConfigurations().then(() => {
						this.setSelectedConfigurationId(
							// This fetches allProductData
							selectedConfigurationId
						);
					});
					this.latestPdfLink = json?.data?.offer?.pdf_link || null;
					this.requestCount--;
					this.$dispatch('notification', {
						status: 'success',
						message: 'Ajánlat mentve',
					});
				})
				.catch((error) => {
					console.warn('There was an error saving offer', error);
					this.requestCount--;
					this.$dispatch('notification', {
						status: 'success',
						message: 'Ajánlat mentve',
					});
				});
		} else {
			throw new Error(
				'saveCurrentConfiguration was called without a current configuration'
			);
		}
	},

	downloadLatestPdf() {
		if (this.latestPdfLink !== null) {
			window.open(this.latestPdfLink, '_blank');
		}
	},

	openSendEmailModal() {
		this.$dispatch('modalopenid', {
			modalid: 'send-via-email-modal',
		});
	},

	getOfferName() {
		if (this.allConfigurations === null) {
			throw new Error(
				'getOfferName called before allConfigurations was fetched'
			);
		}
		if (
			this.cartState === null ||
			this.cartState.selectedConfigurationId === null
		) {
			throw new Error(
				'getOfferName called without a selected configuration'
			);
		}

		if (this.allConfigurations === null) {
			throw new Error('fetchAllConfigurations has not been called');
		}
		const cartState = this.cartState;
		return (
			this.allConfigurations.find(
				(config) => config.id === cartState.selectedConfigurationId
			)?.offer_name || ''
		);
	},

	checkEmailFormThen(callback) {
		this.configurationErrorMessages = [];
		const customerNameNormalized = this.configurationCustomerName.trim();
		const customerEmailNormalized = this.configurationCustomerEmail.trim();
		const customerCommentNormalized = this.configurationComment.trim();
		let error = false;
		if (customerNameNormalized.length < 1) {
			this.configurationErrorMessages = [
				'Kérem, adja meg az ügyfél nevét.',
			];
			error = true;
		}
		if (customerEmailNormalized.length < 1) {
			this.configurationErrorMessages.push(
				'Kérem, adja meg az ügyfél e-mail címét.'
			);
			error = true;
		} else {
			const emailValidation = validEmail(customerEmailNormalized);
			if (!emailValidation.valid) {
				this.configurationErrorMessages.push(
					emailValidation.errorMessage
				);
				error = true;
			}
		}
		if (!error) {
			callback(
				customerNameNormalized,
				customerEmailNormalized,
				customerCommentNormalized
			);
		}
	},

	sendViaEmailFirstInsert() {
		if (this.firstInsertCartState === null) {
			throw new Error(
				'sendViaEmailFirstInsert called without a firstInsertCartState'
			);
		}
		const firstInsertCartState = this.firstInsertCartState;
		this.checkEmailFormThen(
			(
				customerNameNormalized,
				customerEmailNormalized,
				customerCommentNormalized
			) => {
				this.configurationErrorMessages = [];
				const configname = this.configurationNameInput.trim();
				if (configname.length >= 3) {
					this.requestCount++;
					fetch(`/api/offers/send`, {
						method: 'POST',
						headers: {
							accept: 'application/json',
							'content-type': 'application/json',
						},
						body: JSON.stringify({
							offer_name: configname,
							customer_name: customerNameNormalized,
							customer_email: customerEmailNormalized,
							comment: customerCommentNormalized,
							cart: [
								{
									id: firstInsertCartState.id,
									subproductId:
										firstInsertCartState.subproductId,
								},
							],
							attachments: [],
						}),
					})
						.then((resp) => resp.json())
						.then((json) => {
							this.$dispatch('modalcloseid', {
								modalid: 'send-via-email-first-insert-modal',
							});
							this.$dispatch('modalcloseid', {
								modalid: 'new-cart-modal',
							});
							this.$dispatch('modalopenid', {
								modalid: 'email-send-response-modal',
							});
							this.fetchAllConfigurations().then(() =>
								this.setSelectedConfigurationId(
									json?.data?.offer.id || null
								)
							);
							this.requestCount--;
						})
						.catch((error) => {
							console.warn(
								'There was an error sending offer',
								error
							);
							this.requestCount--;
							this.$dispatch('notification', {
								message:
									'Hiba történt az email küldése közben.',
								status: 'error',
							});
						});
				} else {
					this.configurationErrorMessages = [
						'A konfiguráció neve legalább 3 karakter hosszú legyen.',
					];
				}
			}
		);
	},

	sendViaEmail() {
		if (
			this.cartState === null ||
			this.cartState.configurationStates === null
		) {
			throw new Error('cartState was invalid');
		}
		const configurationStates = this.cartState.configurationStates;
		this.checkEmailFormThen(
			(
				customerNameNormalized,
				customerEmailNormalized,
				customerCommentNormalized
			) => {
				const offerid = this.cartState?.selectedConfigurationId || null;
				if (offerid !== null) {
					this.requestCount++;
					fetch(`/api/offers/${offerid}/send`, {
						method: 'PATCH',
						headers: {
							accept: 'application/json',
							'content-type': 'application/json',
						},
						body: JSON.stringify({
							offer_name: this.getOfferName(),
							customer_name: customerNameNormalized,
							customer_email: customerEmailNormalized,
							comment: customerCommentNormalized,
							cart: configurationStates.localCartState,
							attachments: configurationStates.localAttachments.map(att => att.id),
						}),
					})
						.then((resp) => resp.json())
						.then(() => {
							this.$dispatch('modalcloseid', {
								modalid: 'send-via-email-modal',
							});
							this.$dispatch('modalopenid', {
								modalid: 'email-send-response-modal',
							});
							this.fetchAllConfigurations();
							this.requestCount--;
						})
						.catch((error) => {
							console.warn(
								'There was an error sending offer',
								error
							);
							this.requestCount--;
							this.$dispatch('notification', {
								message:
									'Hiba történt az email küldése közben.',
								status: 'error',
							});
						});
				}
			}
		);
	},

	modalclose(id) {
		switch (id) {
			case 'cart-modal': {
				if (this.isConfigurationStateSameOnServer()) {
					this.$dispatch('modalcloseid', {
						modalid: 'cart-modal',
					});
				} else {
					this.modalToBeClosedId = 'cart-modal';
					this.$dispatch('modalopenid', {
						modalid: 'close-confirmation-modal',
					});
				}
				break;
			}
			case 'new-empty-cart-modal': {
				if (this.configurationNameInput.length > 0) {
					this.modalToBeClosedId = 'new-empty-cart-modal';

					this.$dispatch('modalopenid', {
						modalid: 'close-confirmation-modal',
					});
				} else {
					this.$dispatch('modalcloseid', {
						modalid: 'new-empty-cart-modal',
					});
					this.configurationNameInput = '';
					this.configurationErrorMessages = [];
				}
				break;
			}
			case 'new-cart-modal': {
				if (
					this.configurationNameInput.length > 0 ||
					this.configurationComment.length > 0
				) {
					this.modalToBeClosedId = 'new-cart-modal';

					this.$dispatch('modalopenid', {
						modalid: 'close-confirmation-modal',
					});
				} else {
					this.$dispatch('modalcloseid', {
						modalid: 'new-cart-modal',
					});
					this.configurationNameInput = '';
					this.configurationErrorMessages = [];
				}
				break;
			}
			case 'send-via-email-modal': {
				if (
					this.cartState === null ||
					this.cartState.configurationStates === null
				) {
					this.$dispatch('modalcloseid', {
						modalid: 'send-via-email-modal',
					});
					this.configurationNameInput = '';
					this.configurationErrorMessages = [];
					return;
				}
				const configurationStates = this.cartState.configurationStates;
				if (
					this.configurationCustomerName !==
					(this.cartState.configurationStates
						.serverCustomerName || '') ||
					this.configurationCustomerEmail !==
					(this.cartState.configurationStates
						.serverCustomerEmail || '') ||
					!this.cartState.configurationStates.localAttachments.every(
						(val, index) =>
							configurationStates.serverAttachments[index].id === val.id
					)
				) {
					this.modalToBeClosedId = 'send-via-email-modal';
					this.$dispatch('modalopenid', {
						modalid: 'close-confirmation-modal',
					});
				} else {
					this.$dispatch('modalcloseid', {
						modalid: 'send-via-email-modal',
					});
					this.configurationNameInput = '';
					this.configurationErrorMessages = [];
				}

				break;
			}
			case 'send-via-email-first-insert-modal': {
				if (
					this.configurationCustomerName.length > 0 ||
					this.configurationCustomerEmail.length > 0
				) {
					this.modalToBeClosedId =
						'send-via-email-first-insert-modal';
					this.$dispatch('modalopenid', {
						modalid: 'close-confirmation-modal',
					});
				} else {
					this.$dispatch('modalcloseid', {
						modalid: 'send-via-email-first-insert-modal',
					});
					this.configurationNameInput = '';
					this.configurationErrorMessages = [];
				}

				break;
			}
			default: {
				console.warn(
					`Modal close event for ${id} is not implemented yet.`
				);
				this.$dispatch('modalcloseid', { modalid: id });
			}
		}
	},

	modalCloseConfirmed(id) {
		switch (id) {
			case 'cart-modal': {
				this.$dispatch('modalcloseid', {
					modalid: 'cart-modal',
				});
				this.setSelectedConfigurationId(
					this.cartState?.selectedConfigurationId || null
				);
				this.configurationComment =
					this.cartState?.configurationStates?.serverComment || '';
				break;
			}
			case 'new-empty-cart-modal': {
				this.$dispatch('modalcloseid', {
					modalid: 'new-empty-cart-modal',
				});
				this.configurationNameInput = '';
				this.configurationErrorMessages = [];
				break;
			}
			case 'new-cart-modal': {
				this.$dispatch('modalcloseid', {
					modalid: 'new-cart-modal',
				});
				this.configurationNameInput = '';
				this.configurationComment = '';
				this.configurationErrorMessages = [];

				break;
			}
			case 'send-via-email-modal': {
				this.$dispatch('modalcloseid', {
					modalid: 'send-via-email-modal',
				});
				this.configurationCustomerName =
					this.cartState?.configurationStates?.serverCustomerName ||
					'';
				this.configurationCustomerEmail =
					this.cartState?.configurationStates?.serverCustomerEmail ||
					'';
				if (
					this.cartState !== null &&
					this.cartState.configurationStates !== null
				) {
					this.cartState.configurationStates.localAttachments =
						this.cartState?.configurationStates
							?.serverAttachments || [];
				}
				break;
			}
			case 'send-via-email-first-insert-modal': {
				this.$dispatch('modalcloseid', {
					modalid: 'send-via-email-first-insert-modal',
				});
				this.configurationCustomerName = '';
				this.configurationCustomerEmail = '';
				break;
			}
		}
	},

	deleteItem(index) {
		if (
			this.cartState === null ||
			this.cartState.configurationStates === null ||
			(this.cartState.configurationStates.localCartState[index] ||
				null) === null
		) {
			throw new Error('Trying to delete an item with no cart.');
		}
		this.cartState.configurationStates.localCartState.splice(index, 1);
		this.fetchAllProductData();
	},

	insertItemIntoNew() {
		if (this.itemInsertionId !== null) {
			this.firstInsertCartState = {
				id: this.itemInsertionId,
				subproductId: this.itemInsertionSubproductId,
			};
		}
		this.itemInsertionId = null;
		this.itemInsertionSubproductId = null;
		this.firstInsertCartItemInfo = {
			status: 'loading',
		};
		this.loadFirstInsertCartItemsInfo();
		this.$dispatch('modalopenid', {
			modalid: 'new-cart-modal',
		});
	},

	loadFirstInsertCartItemsInfo() {
		if (this.firstInsertCartState === null) {
			throw new Error(
				'LoadFirstInsertCartItemsInfo called with no item id.'
			);
		}
		const id = this.firstInsertCartState.id;
		const siblingId = this.firstInsertCartState.subproductId;
		fetch(`/api/products/${id}/siblings-and-parent`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
		})
			.then((resp) => resp.json())
			.then((json) => {
				if (siblingId === null) {
					this.firstInsertCartItemInfo = {
						status: 'success',
						data: json.data,
						configurationInfo: null,
						configurationOptionState: null,
					};
				} else {
					this.firstInsertCartItemInfo = {
						status: 'success',
						data: json.data,
						configurationInfo: this.getConfigurableData(
							json.data.siblings
						),
						configurationOptionState:
							this.getConfigurationStateFromSibling(
								siblingId,
								json.data.siblings
							),
					};
				}
			})
			.catch((err) => {
				console.warn(
					'There was an error fetching the product data.',
					err
				);
				this.firstInsertCartItemInfo = {
					status: 'failure',
					error: err,
				};
			});
	},
	bind,
});
